<template>
<div class="chapter">
<div class="inner_page">
<h1 class="cn"><a href="#/CityBoy_toc#chap-1">ONE</a></h1>
<h1 class="ct"><a href="#/CityBoy_toc#chap-1"><em>The First Step in the Mending of a Broken Heart</em></a></h1>
<p class="cotx"><span class="dropcap">O</span>n a golden May morning in the sixth year of Calvin Coolidge's presidency, a stout little dark-haired boy named Herbert Bookbinder,
         dressed in a white shirt, a blue tie and gray knee breeches, sat at a desk in Public School 50 in the Bronx, suffering the
         pain of a broken heart. On the blackboard before his eyes were words that told a disaster:
      </p>
<p class="dt"><em>Mrs. Mortiner Gorkin</em></p>
<p>The teacher of Class 7B-1 had just informed her pupils that they must call her “Miss Vernon” no more. Turning to the board
         with a shy smile, she had written her new name in rounded chalk letters, and had blushed through a minute's tumult of squeals
         and giggles from the girls and good-natured jeers from the boys. Then Mrs. Gorkin stilled the noise with an upraised hand.
         She pulled down into view a map of Africa rolled up at the top of the blackboard like a window shade, and the class, refreshed
         by the brief lawbreaking, listened eagerly to her tale of the resources of the Congo. But Herbert could not rouse himself
         to an interest in rubber, gold, apes, and ivory; not when the lost Diana Vernon was talking about them. The tones of her voice
         made him too unhappy.
      </p>
<p>The anodyne in this boy's life was food. No anguish was so sharp that eating could not allay it. Unfortunately lunch time
         was half an hour off. His hand groped softly into his desk and rested on a brown paper bag. He felt the familiar outlines
         of two rolls (today was Monday—lettuce and tomato sandwiches) and an apple. Then his hand encountered something small and
         oval. With practiced, noiseless fingers he opened the bag, unwrapped some twisted wax paper, and drew out a peeled hardboiled
         egg. This was a pretty dry morsel without salt and bread and butter, but the boy popped the whole egg into his mouth and chewed
         it moodily. Like an aspirin, it dulled the pain without improving his spirits. He was aware that his cheeks bulged, but he
         did not care. Let her catch him! He was her favorite, first on the honor roll, and she could not humiliate him without humiliating
         herself more. The boy's calculation was correct. Mrs. Gorkin did see him eating, but she ignored it.
      </p>
<p>In time a beautiful sound rang out—freedom, proclaimed by the clanging of the gong for lunch. At a nod from Mrs. Gorkin, the
         children who had worrisome mothers ran to a shallow closet and returned to their seats wearing coats, while those who had
         braved the changeable May weather without coats sat back and gloried in their maturity. A second time the gong sounded. The
         pupils stood and quietly began to form a double line at the front of the room. Herbie, on his way to the head of the line,
         passed the teacher's desk. She whispered, “Remain behind, Herbert.” Pretending to have heard nothing, Herbie strolled back
         to his desk and remained there fussing busily until the class marched out.
      </p>
<p>A classroom always seems three times larger when the children leave it, and quite bleak. This gives a delicious sense of comradeship
         to two people left in it together. For months it had been Herbie Bookbinder's good luck to share this sweetness with Miss
         Diana Vernon after classes. She had detained him for such honorable offices as putting away books, filling inkwells, closing
         windows with a long hooked pole, and drawing the heavy brown canvas shades; while she combed her long red hair before her
         closet mirror in the late-afternoon sunlight and chatted with him. It had been magical. Being alone in the room now brought
         these memories vividly to the boy. When the teacher re-entered the classroom she found her star pupil seated at his desk,
         his chin resting on his clenched fists, gazing downward at nothing.
      </p>
<p>The cause of his pain was a slim woman, possibly twenty-seven, with compressed lips, a thin little straight nose, and heavy
         red hair. She looked, and she was, strict. But she was a woman, and therefore susceptible to male charm, such as inhered in
         Herbie—and, unfortunately, in Mr. Mortimer Gorkin. The boy glanced at her and felt a pang of self-pity. He could tell by her
         soft look that she felt sorry for him and wanted to comfort him. Immediately he resolved not to be comforted at any cost.
      </p>
<p>“Herbie,” she said, walking to her desk and drawing a metal lunch box out of a deep side drawer, “come here and talk to me
         while I eat.”
      </p>
<p>The boy rose, walked to the front of the desk, and stood there with morose formality, his arms at his sides.</p>
<p>“Come,” said the teacher, “where's your lunch? Or do you want some of mine?”</p>
<p>“I'm not hungry,” said Herbert Bookbinder, looking away from her to the corner of the blackboard where his name headed a list
         of three in golden chalk—the honor roll for April. He decided vengefully that he would be last in the class in May.
      </p>
<p>“It seems to me,” said Mrs. Gorkin, laughing a little, “that you were almost too hungry during the geography lesson—now, weren't
         you?”
      </p>
<p>Herbie stood on his constitutional rights and did not testify.</p>
<p>“What's the matter, Herbie, really?” asked the teacher.</p>
<p>“Nothing.”</p>
<p>“Oh, yes, there is.”</p>
<p>“Oh, no, there isn't—<em>Mrs. Gorkin.</em>”
      </p>
<p>The shot went home; the teacher colored a little. Perhaps pretty Diana Vernon was herself not quite happy about becoming Mrs.
         Gorkin. The name still rang strangely in the bride's ears.
      </p>
<p>“Herbie,” said the teacher with an uncomfortable smile, “even though I'm Mrs. Gorkin now, we're still friends, aren't we?”</p>
<p>(The injured male may be eleven or fifty; the approach of the injuring female does not vary.)</p>
<p>“Sure,” said Herbie dolefully. He hitched up his sagging gray kneepants.</p>
<p>“Someday,” said Mrs. Gorkin, “I hope you will meet Morti—that is, Mr. Gorkin. He's assistant principal at Public School Seventy-five.
         I know he'd like you. He admires clever young men.”
      </p>
<p>Herbie saw through the compliment with contempt. “Sure,” he said again.</p>
<p>The erstwhile Diana Vernon said, “Come closer, Herbie.” The boy reluctantly obeyed, sidling along the edge of the desk, his
         hand resting on top. The teacher put her hand on his. He jerked it away.
      </p>
<p>“When you are as old as I am, Herbie,” said Diana Gorkin softly, “you will be a handsomer man than my husband, and you will
         marry a finer woman than I am, and I hope you'll remember to bring her back here and let me meet her, but I doubt that you
         will.”
      </p>
<p>This speech had no meaning at all for Herbie, who knew perfectly well that he would never be as old as a teacher. “Sure,”
         he said once more. Mrs. Gorkin unwrapped a sandwich, and acknowledged defeat by a curt dismissal. The boy retreated to his
         desk, snatched his lunch bag, and scurried from the classroom.
      </p>
<p>Once outside, he stopped, assumed a dignified air, and pinned around his right arm a yellow strip of flannel decorated with
         three silver stars. He then sauntered along the deserted corridor to the boys' staircase. An ordinary lad at this hour was
         required by law to go without delay to the lunchroom or playground, under pain of receiving a purple slip for loitering. But
         Herbert could choose, in all the huge building, a private place for his noonday meal.
      </p>
<p>Herbert, you see, was one of those privileged beings of the school world, a head monitor. He was captain of the Social Service
         Squad. This was not at all the same thing as the dread Police Squad, of course, whose members stood at gates, doorways, and
         turns in corridors, shouting, “Double up! Hurry up! No talking!” The police could pounce on offenders with the fearsome green
         slips which meant wrath from on high, but Herbie's Social Service Squad had no power of arrest. Its members were assigned
         to various areas of the school, and their duties were simply to keep the building and yards clean. So the squad had irreverently
         been dubbed “the Garbage Gang” by the members of the police, who never tired of pointing the contrast between the might in
         their red armbands and the feeble symbolism of the yellow armbands of Herbie's squad.
      </p>
<p>Since the police were recruited from the tallest, huskiest students, Herbert had despaired of gaining the red band, and had
         therefore worked his way to the top of the Social Service Squad. He figured that if one was not destined for the proud life
         of a wolf, it was still better to be a dog than one of the helpless sheep. This proved perfectly sound. As captain of his
         squad he could rove everywhere on the pretense of inspection. He could come late and walk unchallenged through any gate he
         chose. All at once he had stopped accumulating the orange slips for tardiness which had plagued his days since kindergarten.
         His monthly conduct mark raised itself above B-minus for the first time in his career. Let the ignoramuses rave at him with
         their taunts of “Garbage King!” Herbie had found one of the great secrets of life, the immunity from public law that comes
         of being a public official, and he was fully enjoying the fruits of his discovery.
      </p>
<p>He skipped down the stairs, which echoed with metallic hollowness, to the third floor. Coming upon the brown leather-covered,
         brass-studded door of the auditorium, he decided that the big empty hall suited his melancholy mood. He pushed the door open,
         walked across the rear of the hall to one of the broad windows, curled up on a sill in the sunshine, and opened his bag of
         lunch with a sigh as nearly expressive of contentment as a broken heart would permit.
      </p>
<p>At this moment, through the small, high window of the door leading to the girls' staircase, he caught a glimpse of red curly
         hair gleaming in the sunlight. Craning his neck, he saw that it belonged to a well-dressed, pretty girl about eleven years
         old.
      </p>
</div>
</div>
</template>
<script>

export default {
  name: 'chapter',
  data() {
    return {}
    },
      components: {
    }
  }
</script>



// WEBPACK FOOTER //
// CityBoy_chap-1.vue?81d87dc6